import { useEffect, useState } from "react";
import useCrmApi from "../../../components/hook/crm-req";
import { TraitCategory } from "./data";
import { MockCustomerProfileSummary } from "../profile-explorer/api";

// Traits mock API — see docs/traits-mock-api.md and
// docs/traits-mock-ui-integration.md. Read-only and authenticated; there is
// no create/edit/delete/recalculate/publish endpoint yet, so every mutation
// control in the UI stays a local, clearly-labeled demo action.

export type TraitApiCategory = "declared" | "behavioral" | "predictive";
export type TraitStatus = "active" | "draft";

export interface TraitDefinition {
  key: string;
  name: string;
  category: TraitApiCategory;
  category_label: string;
  data_type: string;
  description: string;
  source_tables: string[];
  source_fields: string[];
  logic: string;
  refresh: string;
  sample_value: unknown;
  coverage: number;
  last_updated: string;
  status: TraitStatus;
}

export interface TraitCategorySummary {
  category: TraitApiCategory;
  label: string;
  count: number;
  description: string;
  trait_keys: string[];
}

export interface TraitUsage {
  key: "segments" | "journeys" | "profile_views";
  label: string;
  description: string;
}

export interface TraitOverviewResponse {
  generated_at: string;
  categories: TraitCategorySummary[];
  recent_traits: TraitDefinition[];
  usage: TraitUsage[];
}

export interface TraitListResponse {
  data: TraitDefinition[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface AttributeDefinition {
  key: string;
  label: string;
  data_type: string;
  source: string;
  used_for: string;
  sample_value: unknown;
}

export interface AttributeListResponse {
  data: AttributeDefinition[];
  total: number;
}

export interface TraitSourceDefinition {
  source_system: string;
  warehouse_tables: string[];
  key_fields: string[];
}

export interface TraitSourceListResponse {
  identity_key: string;
  identity_mapping_table: string;
  data: TraitSourceDefinition[];
  total: number;
}

export interface SegmentDefinition {
  key: string;
  name: string;
  rule: string;
  required_traits: string[];
}

export interface SegmentListResponse {
  data: SegmentDefinition[];
  total: number;
}

// The UI's own category labels ("Custom"/"Behavioral"/"Predictive", used for
// chip colors and the local trait-builder) vs. the API's wire values
// ("declared"/"behavioral"/"predictive") — filters must send the API value,
// never the display label.
export const CATEGORY_TO_API: Record<TraitCategory, TraitApiCategory> = {
  Custom: "declared",
  Behavioral: "behavioral",
  Predictive: "predictive",
};
export const CATEGORY_FROM_API: Record<TraitApiCategory, TraitCategory> = {
  declared: "Custom",
  behavioral: "Behavioral",
  predictive: "Predictive",
};

export function formatSampleValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function formatCoverage(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function formatTimestamp(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("authToken")}` });

const errorFor = (status: number, fallback: string): string => {
  if (status === 403) return "You don't have access to view this.";
  if (status === 422) return "Those filters aren't supported.";
  return fallback;
};

// ─── Overview (landing page's one blocking request) ────────────────────────
export function useTraitsOverview() {
  const { makeRequest } = useCrmApi("/traits/overview", "GET", authHeaders());
  const [data, setData] = useState<TraitOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      setLoading(false);
      if (status >= 200 && status < 300) setData(json);
      else setError(errorFor(status, "Couldn't load the traits overview."));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  return { data, loading, error, retry: () => setReloadKey((k) => k + 1) };
}

// ─── Catalog: search / category filter / pagination ────────────────────────
// Pass `null` to skip fetching (e.g. while the landing page is still showing
// the overview's recent-traits list rather than the full catalog).
export interface TraitListFilters {
  category?: TraitApiCategory;
  search?: string;
  page: number;
  perPage: number;
}

export function useTraitsList(filters: TraitListFilters | null) {
  const { makeRequest } = useCrmApi("/traits", "GET", authHeaders());
  const [data, setData] = useState<TraitListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!filters) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const params: Record<string, any> = { page: filters.page, per_page: filters.perPage };
      if (filters.category) params.category = filters.category;
      if (filters.search?.trim()) params.search = filters.search.trim().slice(0, 100);
      const [json, status] = await makeRequest(undefined, params);
      if (cancelled) return;
      setLoading(false);
      if (status >= 200 && status < 300) setData(json);
      else setError(errorFor(status, "Couldn't load traits."));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.category, filters?.search, filters?.page, filters?.perPage, reloadKey]);

  return { data, loading, error, retry: () => setReloadKey((k) => k + 1) };
}

// ─── Trait detail ───────────────────────────────────────────────────────────
export function useTraitDetail(traitKey: string | null) {
  const { makeRequest } = useCrmApi("/traits", "GET", authHeaders());
  const [data, setData] = useState<TraitDefinition | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!traitKey) {
      setData(null);
      setNotFound(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);
    (async () => {
      const [json, status] = await makeRequest(undefined, undefined, encodeURIComponent(traitKey));
      if (cancelled) return;
      setLoading(false);
      if (status === 200) setData(json);
      else if (status === 404) setNotFound(true);
      else setError(errorFor(status, "Couldn't load this trait."));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traitKey]);

  return { data, loading, notFound, error };
}

// ─── Attributes / sources / segments — small, unpaginated lists ────────────
function useTraitStaticList<T>(endpoint: string, extract: (json: any) => T) {
  const { makeRequest } = useCrmApi(endpoint, "GET", authHeaders());
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [json, status] = await makeRequest();
      if (cancelled) return;
      setLoading(false);
      if (status >= 200 && status < 300) setData(extract(json));
      else setError(errorFor(status, "Couldn't load this."));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading, error };
}

export function useTraitAttributes() {
  return useTraitStaticList<AttributeListResponse>("/traits/attributes", (j) => j);
}

export function useTraitSources() {
  return useTraitStaticList<TraitSourceListResponse>("/traits/sources", (j) => j);
}

export function useTraitSegments() {
  return useTraitStaticList<SegmentListResponse>("/traits/segments", (j) => j);
}

// ─── Sample customers for the trait preview step ───────────────────────────
// /traits/customers is now dedicated to the Profile Explorer (see
// ../profile-explorer/api.ts, same doc's full 250-profile dataset with
// search/filters/pagination). The trait builder just needs a handful of real
// names to preview against, so it borrows the same endpoint with a small
// per_page rather than fetching (or duplicating types for) the full list.
export function useSampleProfiles(count = 3) {
  const { makeRequest } = useCrmApi("/traits/customers", "GET", authHeaders());
  const [data, setData] = useState<MockCustomerProfileSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [json, status] = await makeRequest(undefined, { page: 1, per_page: count });
      setLoading(false);
      if (status >= 200 && status < 300) setData(Array.isArray(json?.data) ? json.data : []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  return { data, loading };
}
